import React, {useLayoutEffect, useRef} from "react";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import classNames from "classnames";
import {DateTime} from "luxon";
import {Week} from "./Week";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {AziendaRecord} from "@commons/mapping/AziendaRecord";
import styles from "../pianificazione.module.css";
import {badgeStyle} from "@commons/components/BasicBadge";
import BasicBadge, {BadgeStyle} from "@components/BasicBadge";
import {CentroDiCostoRecord} from "@commons/mapping/CentroDiCostoRecord";
import TooltipEx from "@components/Tooltip";
import RiepilogoIC from "../../comps/RiepilogoIC";
import {IconCircleFilled, IconInfoCircleFilled} from "@tabler/icons-react";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectAzienda} from "@commons/core.slice";

type  SetRef<T> = (node: T | null) => void | undefined;

export const RowLavoratore: React.FC<{
    containerRef?: React.MutableRefObject<HTMLDivElement | null>
    lavoratore: LavoratorePresenzeRecord,
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[],
    index: number,
    showRiepiloghi?: boolean,
    showCalendario?: boolean,
    showOre?: boolean,
    showTurno?: boolean,
    showLavoratore?: boolean,
    onDropFiliale?: (filiale: AziendaRecord, target: PresenzeGiornataPlusRecord | PresenzeGiornataPlusRecord[]) => void,
    onDropTurno?: (turno: TurnoRecord | null, target: PresenzeGiornataPlusRecord | PresenzeGiornataPlusRecord[]) => void,
    onResetTurno?: (target: PresenzeGiornataPlusRecord | PresenzeGiornataPlusRecord[]) => void,
    onDropGiustificativo?: (giustificativo: GiustificativoRecord, target: PresenzeGiornataPlusRecord | PresenzeGiornataPlusRecord[]) => void,
    onClickGiornata?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, target: PresenzeGiornataPlusRecord) => void,
    offset: number,
    mansioni?: CentroDiCostoRecord[]
}> = ({
          lavoratore,
          turni,
          giustificativi,
          index,
          showRiepiloghi = true,
          showCalendario = true,
          showOre = true,
          showTurno = true,
          showLavoratore = true,
          containerRef,
          onDropTurno, onResetTurno, onDropGiustificativo, onDropFiliale, onClickGiornata,
          offset,
          mansioni,
      }) => {
    const azienda = useAppSelector(selectAzienda);
    const weeksRef = useRef<HTMLDivElement | null>(null)
    useLayoutEffect(() => {
        if (weeksRef.current) {
            weeksRef.current.scrollTo({
                left: offset,
                behavior: 'smooth' // "auto" | "smooth";
            });
        }
    }, [offset]);

    if (!lavoratore)
        return <></>;

    const showMansione = showRiepiloghi || showCalendario || showOre;
    let style: BadgeStyle | undefined = undefined;
    if (mansioni && mansioni.length) {
        const mansione = mansioni.find(m => m.id === lavoratore.codMansione);
        if (mansione && mansione.colore)
            style = {bg: mansione.colore!, text: 'text-black'};
    }
    console.log('Cognome: ', index, lavoratore.cognome, lavoratore.id);

    return <div className={classNames(showLavoratore ? styles.rigaLavoratore : "min-h-fit", "bg-white even:bg-gray-100")}>
        <div className="flex flex-row">

            {/* **************************** */}
            {/* *** LAVORATORE CONTAINER *** */}
            <div className={classNames(styles.containerLavoratore, index % 2 ? 'bg-gray-100' : 'bg-white')}>
                <div className="self-center min-w-[140px] max-w-[140px] text-ellipsis overflow-hidden">
                    {showLavoratore && <>
                        <div className="inline-flex whitespace-nowrap">
                            {!showMansione &&
                                lavoratore.codMansione &&
                                <TooltipEx placement="bottom" tooltip={lavoratore.codMansione}>
                                    <BasicBadge label={<span className="invisible">|</span>} type={"custom"}
                                                containerClass="text-sm mr-0.5"
                                                customStyle={style || {bg: `${badgeStyle.primary.bg}`, text: "text‑current"}}/>
                                </TooltipEx>
                            }
                            {!lavoratore.presenteDa && !lavoratore.presenteA && <IconCircleFilled className="my-auto mr-0.5 text-hhSuAltraSede" size={14}/>}
                            {lavoratore.cognome + ' ' + lavoratore.nome}
                        </div>
                        {showMansione &&
                            <div className="inline-flex">
                                {lavoratore.codMansione && <BasicBadge label={lavoratore.codMansione} type={"custom"}
                                                                       containerClass={`text-sm`}
                                                                       customStyle={style || {bg: `${badgeStyle.primary.bg}`, text: "text‑current"}}/>}
                                <TooltipEx placement="bottom" tooltip={
                                    <div className="max-w-min min-w-[144px]">
                                        <RiepilogoIC idAziendaMaster={azienda?.idAziendaMaster}
                                                     idAziendaTabelle={azienda?.idAziendaCDIC}
                                                     idDip={lavoratore.id}
                                                     periodo={{
                                                         anno: lavoratore.anno,
                                                         mese: lavoratore.mese,
                                                     }}
                                                     tabellaAziendaNonConfigurata={<div className="text-red-600">Tabella istituti contrattuali non configurata.</div>}
                                        /></div>
                                }>
                                    <IconInfoCircleFilled className="mx-2 h-5 w-5 text-blue-500"/>
                                </TooltipEx>
                            </div>}
                    </>}
                </div>
            </div>

            {/* *********************** */}
            {/* *** WEEKS CONTAINER *** */}
            <div className={styles.container} ref={r => {
                if (containerRef)
                    containerRef.current = r;
                weeksRef.current = r;
            }}>
                {lavoratore.weeks && lavoratore.days && lavoratore.weeks.map((week, index) => {
                    const inizioWeek = DateTime.fromISO(week.dtInizioWeek!);
                    const days = lavoratore.days!.filter(day => {
                        return DateTime.fromISO(day.dtGiornata!).weekNumber === inizioWeek.weekNumber;
                    });
                    return <Week key={index} lavoratore={showLavoratore ? lavoratore : null} week={week} index={index} days={days} turni={turni}
                                 giustificativi={giustificativi}
                                 showCalendario={showCalendario}
                                 showOre={showOre}
                                 showTurno={showTurno}
                                 showRiepiloghi={showRiepiloghi}
                                 onDropFiliale={onDropFiliale}
                                 onDropTurno={onDropTurno}
                                 onResetTurno={onResetTurno}
                                 onDropGiustificativo={onDropGiustificativo}
                                 onClickGiornata={onClickGiornata}
                    ></Week>
                })}
            </div>

        </div>
    </div>
}